import React, { useState, useEffect } from "react";
import {
  Box, Typography, styled as muiStyled, TextField, Button,
  Grid, Paper, CircularProgress, Menu, MenuItem, Divider
} from "@mui/material";
import * as XLSX from 'xlsx';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from "recharts";
import Autocomplete from "@mui/material/Autocomplete";
import { createGlobalStyle } from "styled-components";
import Tooltip from "@mui/material/Tooltip";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const DatePickerStyles = createGlobalStyle`
  .react-datepicker {
    background-color: white !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 9999 !important;
  }
  .react-datepicker__triangle {
    display: none;
  }
  .react-datepicker__header {
    background-color: white !important;
    border-bottom: 1px solid #ddd;
  }
  .react-datepicker__day {
    color: #333;
  }
  .react-datepicker__day--selected {
    background-color: #1976d2 !important;
    color: white !important;
  }
`;

const Layout = muiStyled(Box)({ display: "flex", minHeight: "100vh", backgroundColor: "#f8f9fa" });
const Sidebar = muiStyled(Box)({ width: "300px", padding: "30px 20px 20px 20px", backgroundColor: "#ffffff", borderRight: "1px solid #ddd" });
const Main = muiStyled(Box)({ flex: 1, padding: "40px" });
const SectionTitle = muiStyled(Typography)({ fontSize: "28px", fontWeight: "bold", color: "#2c3e50", marginBottom: "10px" });
const FilterContainer = muiStyled(Box)({ display: "flex", flexDirection: "column", gap: "50px", marginTop: "10px" });
const SummaryCard = muiStyled(Paper)({
  padding: "20px",
  textAlign: "center",
  backgroundColor: "#ffffff",
  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
  transition: "all 0.3s ease-in-out",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 16px rgba(0,0,0,0.15)",
    backgroundColor: "#f1f9ff"
  }
});

const DownloadButton = muiStyled(Button)({
  textTransform: "none",
  fontWeight: "bold",
  padding: "8px 16px",
  backgroundColor: "#1976d2",
  color: "white",
  '&:hover': {
    backgroundColor: "#1565c0",
  }
});
const StyledDatePickerWrapper = muiStyled("div")({
  width: "100%",
  "& input": {
    width: "100%",
    height: "44px",
    fontSize: "16px",
    padding: "10px 12px",
    border: "1px solid #ccc",
    borderRadius: "4px",
  },
});

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#9575cd", "#f06292", "#4dd0e1", "#aed581"];

function DailyProductivity() {
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRepo, setSelectedRepo] = useState(null);
  const [users, setUsers] = useState([]);
  const [repositories, setRepositories] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const formatDateDisplay = (date) => date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

  const fetchUsersAndRepos = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const [userRes, repoRes] = await Promise.all([
        fetch('https://fztfytovnyxxetzgjzxl.supabase.co/functions/v1/github/users', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch('https://fztfytovnyxxetzgjzxl.supabase.co/functions/v1/github/repositories', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      const usersData = await userRes.json();
      const reposData = await repoRes.json();
      setUsers(Array.isArray(usersData) ? usersData.map((u) => u.login) : []);
      setRepositories(Array.isArray(reposData) ? reposData.map((r) => r.name) : []);
    } catch (err) {
      console.error("❌ Failed to load users or repos:", err);
      setUsers([]);
      setRepositories([]);
    }
  };
  
  const fetchProductivity = async () => {
    setLoading(true);
    setNoData(false);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate.toISOString().split("T")[0]);
      if (endDate) params.append("endDate", endDate.toISOString().split("T"));
      if (selectedUser) params.append("user", selectedUser);
      if (selectedRepo) params.append("repo", selectedRepo);
  
      const token = localStorage.getItem('auth_token');
      const res = await fetch(
        `https://fztfytovnyxxetzgjzxl.supabase.co/functions/v1/github/daily-productivity?${params.toString()}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await res.json();
  
      if (data?.summary) {
        // parsing logic unchanged...
        const baseMetrics = [
          { label: "Issues Opened", value: data.summary.issuesOpened },
          { label: "Issues Closed", value: data.summary.issuesClosed },
          { label: "PRs Opened", value: data.summary.prsOpened },
          { label: "PRs Closed", value: data.summary.prsClosed },
          { label: "PRs Merged", value: data.summary.prsMerged },
          { label: "Commits", value: data.summary.commits },
          { label: "Reviews", value: data.summary.reviews }
        ];
  
        const hasSpecificFilter = selectedUser || selectedRepo;
        let mapped = [...baseMetrics];
  
        if (hasSpecificFilter) {
          mapped.push({
            label: "Productivity %",
            value: data.summary.productivityPercent,
            prs_percent: data.summary.prs_percent || 0,
            issues_percent: data.summary.issues_percent || 0,
            commits_percent: data.summary.commits_percent || 0,
            reviews_percent: data.summary.reviews_percent || 0
          });
        }
        setSummaryData(mapped);
        const allZero = mapped.every(item => {
          const val = typeof item.value === "string" ? item.value.replace("%", "") : item.value;
          return Number(val) === 0;
        });
        setNoData(allZero);
      } else {
        setSummaryData([]);
        setNoData(true);
      }
    } catch (err) {
      console.error("❌ Error fetching productivity:", err);
      setSummaryData([]);
      setNoData(true);
    } finally {
      setLoading(false);
    }
  };
  
  const handleApplyFilters = () => fetchProductivity();
  const handleClearFilters = () => {
    setSelectedUser(null);
    setSelectedRepo(null);
    setStartDate(new Date());
    setEndDate(new Date());
    fetchProductivity();
  };

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleDownload = async (format) => {
    handleCloseMenu();
    setDownloading(true);
    try {
      // Format the data for export - use the data already loaded in the component
      const reportTitle = "GitHub Productivity Report";
      const subtitle = `${formatDateDisplay(startDate)}${startDate.toDateString() !== endDate.toDateString() ? ` to ${formatDateDisplay(endDate)}` : ''}`;
      const filters = [];
      if (selectedUser) filters.push(`User: ${selectedUser}`);
      if (selectedRepo) filters.push(`Repository: ${selectedRepo}`);
      
      // Check if we should include productivity percentage in exports
      const hasSpecificFilter = selectedUser || selectedRepo;
      
      // Prepare the data rows
      const rows = summaryData
        .filter(item => hasSpecificFilter || item.label !== "Productivity %")
        .map(item => ({
        label: item.label,
        value: item.value,
        percentage: item.label === "Productivity %" ? item.value : 
          (item.prs_percent !== undefined ? `${item.prs_percent?.toFixed(2)}%` : 
          item.issues_percent !== undefined ? `${item.issues_percent?.toFixed(2)}%` : 
          item.commits_percent !== undefined ? `${item.commits_percent?.toFixed(2)}%` : 
          item.reviews_percent !== undefined ? `${item.reviews_percent?.toFixed(2)}%` : "")
      }));
      
      // Generate filename
      const timestamp = new Date().toISOString().split('T')[0];
      const userPart = selectedUser ? `_user_${selectedUser}` : '';
      const repoPart = selectedRepo ? `_repo_${selectedRepo}` : '';
      const filename = `github_productivity${userPart}${repoPart}_${timestamp}.${format}`;
      
      if (format === 'csv') {
        // Generate CSV
        const csvContent = [
          ["Metric", "Count", "Percentage"],
          ...rows.map(row => [row.label, row.value, row.percentage])
        ].map(row => row.join(",")).join("\n");
        
        downloadFile(csvContent, filename, "text/csv");
      } 
      else if (format === 'excel') {
        try {
          // Create worksheet data
          const wsData = [
            ["Metric", "Count", "Percentage"],
            ...rows.map(row => [row.label, row.value, row.percentage])
          ];
          
          // Create a worksheet
          const ws = XLSX.utils.aoa_to_sheet(wsData);
          
          // Create a workbook with the worksheet
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, "GitHub Productivity");
          
          // Add metadata
          wb.Props = {
            Title: reportTitle,
            Subject: subtitle,
            Author: "DevOps Dashboard",
            CreatedDate: new Date()
          };
          
          // Generate Excel file
          const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
          
          // Convert to Blob and download
          const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          const url = URL.createObjectURL(blob);
          
          // Download the file
          const link = document.createElement('a');
          link.href = url;
          link.download = filename.replace('.excel', '.xlsx');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } catch (error) {
          console.error("❌ Error generating Excel file:", error);
          alert("Failed to generate Excel file. Falling back to CSV format.");
          
          // Fallback to CSV if Excel generation fails
          const csvContent = [
            ["Metric", "Count", "Percentage"],
            ...rows.map(row => [row.label, row.value, row.percentage])
          ].map(row => row.join(",")).join("\n");
          
          downloadFile(csvContent, filename.replace('.excel', '.csv'), "text/csv");
        }
      }
      else if (format === 'pdf') {
        // For PDF, we would normally use a library like jsPDF
        // But for simplicity, we'll create an HTML version that can be printed to PDF
        
        // Create a canvas element to render the pie chart
        const chartData = summaryData.filter(item => item.label !== "Productivity %");
        
        // Create color mapping for the chart
        const colorMap = {};
        chartData.forEach((item, index) => {
          colorMap[item.label] = COLORS[index % COLORS.length];
        });
        
        // Create a simple SVG pie chart
        const createPieChart = () => {
          const total = chartData.reduce((sum, item) => sum + Number(item.value), 0);
          if (total === 0) return '';
          
          let startAngle = 0;
          const radius = 100;
          const cx = 150;
          const cy = 150;
          const svgSize = 300;
          
          const slices = chartData.map(item => {
            const value = Number(item.value);
            const percentage = (value / total) * 100;
            const angle = (percentage / 100) * 360;
            const endAngle = startAngle + angle;
            
            // Calculate the SVG path for the slice
            const x1 = cx + radius * Math.cos(Math.PI * startAngle / 180);
            const y1 = cy + radius * Math.sin(Math.PI * startAngle / 180);
            const x2 = cx + radius * Math.cos(Math.PI * endAngle / 180);
            const y2 = cy + radius * Math.sin(Math.PI * endAngle / 180);
            
            const largeArcFlag = angle > 180 ? 1 : 0;
            
            const pathData = [
              `M ${cx},${cy}`,
              `L ${x1},${y1}`,
              `A ${radius},${radius} 0 ${largeArcFlag},1 ${x2},${y2}`,
              'Z'
            ].join(' ');
            
            const slice = `<path d="${pathData}" fill="${colorMap[item.label]}" stroke="white" stroke-width="1"></path>`;
            
            startAngle = endAngle;
            return slice;
          }).join('');
          
          // Calculate better spacing for legend items
          const calculateItemWidth = (item) => {
            // Estimate text width based on character count (rough approximation)
            const textLength = `${item.label} (${item.value})`.length;
            return Math.max(150, textLength * 7 + 30); // Base width + character width
          };
          
          // Calculate positions with proper spacing
          const positions = [];
          let currentX = 20;
          
          chartData.forEach(item => {
            const width = calculateItemWidth(item);
            positions.push({ x: currentX, width });
            currentX += width;
          });
          
          // Create horizontal legend
          const legend = chartData.map((item, index) => {
            const { x } = positions[index];
            return `
              <g transform="translate(${x}, 320)">
                <circle cx="7" cy="7" r="7" fill="${colorMap[item.label]}"></circle>
                <text x="20" y="10" font-size="12" dominant-baseline="middle">${item.label} (${item.value})</text>
              </g>
            `;
          }).join('');
          
          // Calculate total width needed
          const totalWidth = positions.length > 0 ? 
            positions[positions.length - 1].x + positions[positions.length - 1].width : 
            svgSize;
          const legendWidth = Math.max(svgSize, totalWidth + 40);
          
          return `
            <svg width="${legendWidth}" height="350" xmlns="http://www.w3.org/2000/svg">
              <g transform="translate(${legendWidth/2 - 150}, 0)">
                ${slices}
              </g>
              <g>
                ${legend}
              </g>
            </svg>
          `;
        };
        
        const pieChartSvg = createPieChart();
        
        const htmlContent = `
          <html>
            <head>
              <title>${reportTitle}</title>
              <style>
                body { font-family: 'Proxima Nova', sans-serif; padding: 20px; }
                h1 { text-align: center; color: #2c3e50; }
                h3 { color: #7f8c8d; margin-bottom: 30px; text-align: center; }
                .filters { margin-bottom: 20px; color: #7f8c8d; }
                table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
                th { background-color: #f8f9fa; text-align: left; padding: 12px; border-bottom: 2px solid #ddd; }
                td { padding: 12px; border-bottom: 1px solid #ddd; }
                .footer { color: #7f8c8d; text-align: right; font-size: 12px; margin-top: 30px; }
                .chart-container { text-align: center; margin: 30px 0; }
                h2 { color: #2c3e50; text-align: center; margin-top: 40px; }
              </style>
            </head>
            <body>
              <h1>${reportTitle}</h1>
              <h3>${subtitle}</h3>
              ${filters.length > 0 ? `<div class="filters">Filters: ${filters.join(' | ')}</div>` : ''}
              <table>
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Count</th>
                    <th>Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  ${rows.map(row => `
                    <tr>
                      <td>${row.label}</td>
                      <td>${row.value}</td>
                      <td>${row.percentage}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
              
              <h2>Distributions</h2>
              <div class="chart-container">
                ${pieChartSvg}
              </div>
              
              <div class="footer">Generated on: ${new Date().toLocaleString()}</div>
            </body>
          </html>
        `;
        
        // Open in a new window that can be printed to PDF
        const printWindow = window.open('', '_blank');
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 500);
      }
    } catch (error) {
      console.error(`❌ Error downloading ${format} file:`, error);
      alert(`Failed to download ${format.toUpperCase()} file. Please try again.`);
    } finally {
      setDownloading(false);
    }
  };
  
  // Helper function to download a file
  const downloadFile = (content, filename, contentType) => {
    const blob = new Blob([content], { type: contentType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchUsersAndRepos();
    fetchProductivity();
  }, []);

  return (
    <>
      <DatePickerStyles />
      <Layout>
        <Sidebar>
          <Typography variant="h6" mb={1}>Filter Productivity Data</Typography>
          <FilterContainer>
            <Box>
              <Typography variant="body2">Start Date</Typography>
              <StyledDatePickerWrapper>
                <DatePicker selected={startDate} onChange={(date) => setStartDate(date)} selectsStart startDate={startDate} endDate={endDate} />
              </StyledDatePickerWrapper>
            </Box>
            <Box>
              <Typography variant="body2">End Date</Typography>
              <StyledDatePickerWrapper>
                <DatePicker selected={endDate} onChange={(date) => setEndDate(date)} selectsEnd startDate={startDate} endDate={endDate} minDate={startDate} />
              </StyledDatePickerWrapper>
            </Box>
            <Autocomplete options={users} value={selectedUser} onChange={(e, newVal) => setSelectedUser(newVal)} renderInput={(params) => <TextField {...params} label="Search User" fullWidth />} />
            <Autocomplete options={repositories} value={selectedRepo} onChange={(e, newVal) => setSelectedRepo(newVal)} renderInput={(params) => <TextField {...params} label="Search Repository" fullWidth />} />
            <Box display="flex" justifyContent="space-between">
              <Button variant="outlined" color="error" onClick={handleClearFilters}>Clear</Button>
              <Button onClick={handleApplyFilters} variant="contained" color="primary">Apply</Button>
            </Box>
          </FilterContainer>
        </Sidebar>
        <Main>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <div>
              <SectionTitle>GitHub Productivity Insights</SectionTitle>
              <Typography variant="subtitle1" color="text.secondary">
                Showing data for: <strong>{formatDateDisplay(startDate)}</strong>
                {startDate.toDateString() !== endDate.toDateString() && <> to <strong>{formatDateDisplay(endDate)}</strong></>}
                {selectedUser && <> | User: <strong>{selectedUser}</strong></>}
                {selectedRepo && <> | Repository: <strong>{selectedRepo}</strong></>}
              </Typography>
            </div>
            
            {!loading && !noData && (
              <div>
                <DownloadButton
                  aria-controls="download-menu"
                  aria-haspopup="true"
                  onClick={handleOpenMenu}
                  disabled={downloading}
                  startIcon={<DownloadIcon />}
                  endIcon={<KeyboardArrowDownIcon />}
                >
                  Download Reports
                </DownloadButton>
                <Menu
                  id="download-menu"
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleCloseMenu}
                  PaperProps={{
                    style: {
                      width: 200,
                      padding: '4px 0'
                    }
                  }}
                >
                  <MenuItem onClick={() => handleDownload('csv')} style={{ padding: '10px 16px' }}>
                    <TableViewIcon style={{ marginRight: 8 }} /> Export in CSV
                  </MenuItem>
                  <MenuItem onClick={() => handleDownload('excel')} style={{ padding: '10px 16px' }}>
                    <InsertDriveFileIcon style={{ marginRight: 8 }} /> Export in Excel
                  </MenuItem>
                  <MenuItem onClick={() => handleDownload('pdf')} style={{ padding: '10px 16px' }}>
                    <PictureAsPdfIcon style={{ marginRight: 8 }} /> Export in PDF
                  </MenuItem>
                </Menu>
              </div>
            )}
          </Box>
          
          {downloading && (
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <CircularProgress size={20} />
              <Typography variant="body2" ml={1} color="text.secondary">
                Preparing download...
              </Typography>
            </Box>
          )}

          {loading && (<Box mt={4} textAlign="center"><CircularProgress /></Box>)}

          {!loading && !noData && (
            <>
              <Grid container spacing={2}>
                {summaryData.map((item, index) => {
                  const isProductivity = item.label === "Productivity %";
                  const tooltipContent = isProductivity ? (
                    <Box>
                      <Typography variant="body2">PRs: {item.prs_percent?.toFixed(2)}%</Typography>
                      <Typography variant="body2">Issues: {item.issues_percent?.toFixed(2)}%</Typography>
                      <Typography variant="body2">Commits: {item.commits_percent?.toFixed(2)}%</Typography>
                      <Typography variant="body2">Reviews: {item.reviews_percent?.toFixed(2)}%</Typography>
                    </Box>
                  ) : "";
                  
                  return (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <Tooltip title={tooltipContent} arrow disableHoverListener={!isProductivity}>
                        <div>
                          <SummaryCard>
                            <Typography variant="h6" color="primary">{item.label}</Typography>
                            <Typography variant="h4" fontWeight="bold">{item.value}</Typography>
                          </SummaryCard>
                        </div>
                      </Tooltip>
                    </Grid>
                  );
                })}
              </Grid>
              <Box mt={5}>
                <Typography variant="h6" fontWeight="bold" mb={2}>Distributions</Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={summaryData.filter(item => item.label !== "Productivity %")} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={100} fill="#8884d8">
                      {summaryData.map((_, index) => (<Cell key={index} fill={COLORS[index % COLORS.length]} />))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </>
          )}

          {!loading && noData && (
            <Box mt={4} textAlign="center">
              <Typography variant="h6" color="text.secondary">⚠️ No contributions found for the selected filters.</Typography>
            </Box>
          )}
        </Main>
      </Layout>
    </>
  );
}

export default DailyProductivity;
